// src/AdminUserInfoTab.js
import React, { useState } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminUserInfoTab = () => {
  const [email, setEmail] = useState('');
  const [memberData, setMemberData] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMemberInfo = async () => {
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setMemberData(null);
    setSelectedAssignment('');
    
    try {
      const response = await authedFetch(apiUrl('adminMemberInfo'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim()
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to fetch member info');
      }

      setMemberData(result.data);
      
      // Auto-select the most recent assignment if available
      if (result.data.dropdownOptions && result.data.dropdownOptions.length > 0) {
        setSelectedAssignment(result.data.dropdownOptions[0].displayName);
      }
      
    } catch (error) {
      console.error('Failed to fetch member info:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchMemberInfo();
    }
  };

  const getSelectedAssignmentData = () => {
  if (!memberData || !selectedAssignment) return null;
  
  return memberData.dropdownOptions.find(
    option => option.displayName === selectedAssignment
  );
};

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Member Information Lookup</h2>
        <p style={descriptionStyle}>
          Enter a member's email address to view their complete profile and number assignment history.
        </p>
      </div>

      {/* Search Form */}
      <div style={searchCardStyle}>
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Member Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="member@example.com"
            style={inputStyle}
            disabled={loading}
          />
        </div>

        <button
          onClick={fetchMemberInfo}
          disabled={loading || !email.trim()}
          style={{
            ...buttonStyle,
            backgroundColor: loading || !email.trim() ? '#ccc' : '#1976d2',
            cursor: loading || !email.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Loading...' : 'Look Up Member'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div style={errorCardStyle}>
          <span style={errorIconStyle}>❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Member Data Display */}
      {memberData && (
        <>
          {/* Account Information */}
          <div style={sectionStyle}>
            <h3>Account Information</h3>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Email:</span>
                <span style={infoValueStyle}>{memberData.profile.accountInfo?.email || 'N/A'}</span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Name:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.accountInfo?.firstName || ''} {memberData.profile.accountInfo?.lastName || ''}
                </span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Phone:</span>
                <span style={infoValueStyle}>{memberData.profile.accountInfo?.phone || 'N/A'}</span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Status:</span>
                <span style={{
  ...infoValueStyle,
  color: memberData.profile.accountInfo?.accountStatus === 'active' ? '#22c55e' : '#ef4444'
}}>
  {(memberData.profile.accountInfo?.accountStatus || 'N/A').toUpperCase()}
</span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>City/State:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.accountInfo?.city && memberData.profile.accountInfo?.state 
                    ? `${memberData.profile.accountInfo.city}, ${memberData.profile.accountInfo.state}`
                    : 'N/A'
                  }
                </span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Signup Date:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.subscription?.signupDate ? 
                    new Date(memberData.profile.subscription.signupDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long' 
                    }) : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Subscription Information */}
          <div style={sectionStyle}>
            <h3>Subscription Details</h3>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Plan:</span>
                <span style={infoValueStyle}>
  {(memberData.profile.subscription?.plan || memberData.profile.plan || 'N/A').toUpperCase()}
</span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Auto Renew:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.subscription?.autoRenew === true ? 'Enabled' : 
                   memberData.profile.subscription?.autoRenew === false ? 'Disabled' : 
                   memberData.profile.autoRenew === true ? 'Enabled' :
                   memberData.profile.autoRenew === false ? 'Disabled' : 'N/A'}
                </span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Stripe Customer ID:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.stripe?.customerId || memberData.profile.stripeCustomerId || 'N/A'}
                </span>
              </div>
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Renewal Date:</span>
                <span style={infoValueStyle}>
                  {memberData.profile.subscription?.renewalDate ? 
                    new Date(memberData.profile.subscription.renewalDate).toLocaleDateString() : 
                    memberData.profile.renewalDate ? 
                    new Date(memberData.profile.renewalDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
{/* Latest Assignments */}
          <div style={sectionStyle}>
            <h3>Latest Number Assignments</h3>
            <div style={latestGridStyle}>
              <div style={latestCardStyle}>
                <h4>Powerball</h4>
                {memberData.latestAssignments.powerball ? (
                  <>
                    <div style={assignmentDateStyle}>
                      {memberData.latestAssignments.powerball.date}
                    </div>
                    <div style={numbersDisplayStyle}>
                      {memberData.latestAssignments.powerball.data.numberSets && 
                       Array.isArray(memberData.latestAssignments.powerball.data.numberSets) ? 
                        memberData.latestAssignments.powerball.data.numberSets.map((set, index) => (
                          <div key={index} style={numberSetStyle}>
                            Set {index + 1}: {set}
                          </div>
                        )) : 'No numbers available'
                      }
                    </div>
                  </>
                ) : (
                  <div style={noAssignmentStyle}>No assignments found</div>
                )}
              </div>
              <div style={latestCardStyle}>
                <h4>Mega Millions</h4>
                {memberData.latestAssignments.megamillions ? (
                  <>
                    <div style={assignmentDateStyle}>
                      {memberData.latestAssignments.megamillions.date}
                    </div>
                    <div style={numbersDisplayStyle}>
                      {memberData.latestAssignments.megamillions.data.numberSets && 
                       Array.isArray(memberData.latestAssignments.megamillions.data.numberSets) ? 
                        memberData.latestAssignments.megamillions.data.numberSets.map((set, index) => (
                          <div key={index} style={numberSetStyle}>
                            Set {index + 1}: {set}
                          </div>
                        )) : 'No numbers available'
                      }
                    </div>
                  </>
                ) : (
                  <div style={noAssignmentStyle}>No assignments found</div>
                )}
              </div>
            </div>
          </div>

{/* Historical Assignments */}
          {memberData.dropdownOptions && memberData.dropdownOptions.length > 0 && (
            <div style={sectionStyle}>
              <h3>Assignment History</h3>
              <div style={historyControlsStyle}>
                <label style={labelStyle}>Select Assignment Date:</label>
                <select
                  value={selectedAssignment}
                  onChange={(e) => setSelectedAssignment(e.target.value)}
                  style={selectStyle}
                >
                  {memberData.dropdownOptions.map((option) => (
                    <option key={option.displayName} value={option.displayName}>
                      {option.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {selectedAssignment && (
                <div style={selectedAssignmentStyle}>
                  <h4>{selectedAssignment}</h4>
                  <div style={numbersDisplayStyle}>
                    {getSelectedAssignmentData()?.data.numberSets && 
                     Array.isArray(getSelectedAssignmentData()?.data.numberSets) ? 
                      getSelectedAssignmentData().data.numberSets.map((set, index) => (
                        <div key={index} style={numberSetStyle}>
                          Set {index + 1}: {set}
                        </div>
                      )) : 'No numbers available'
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  marginBottom: '2rem',
  textAlign: 'center'
};

const descriptionStyle = {
  color: '#666',
  fontSize: '1.1rem',
  marginTop: '0.5rem'
};

const searchCardStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const inputGroupStyle = {
  marginBottom: '1.5rem'
};

const labelStyle = {
  display: 'block',
  fontSize: '1rem',
  fontWeight: '600',
  marginBottom: '0.5rem',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  fontSize: '1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  boxSizing: 'border-box'
};

const buttonStyle = {
  width: '100%',
  padding: '0.875rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  border: 'none',
  borderRadius: '8px'
};

const errorCardStyle = {
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  padding: '1rem',
  marginBottom: '2rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#dc2626'
};

const errorIconStyle = {
  fontSize: '1.2rem'
};

const sectionStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const infoGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1rem',
  marginTop: '1rem'
};

const infoItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px'
};

const infoLabelStyle = {
  fontWeight: '600',
  color: '#333'
};

const infoValueStyle = {
  color: '#666'
};

const latestGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const latestCardStyle = {
  backgroundColor: '#f8f9fa',
  padding: '1.5rem',
  borderRadius: '8px'
};

const assignmentDateStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#1976d2',
  marginBottom: '1rem'
};

const numbersDisplayStyle = {
  fontSize: '0.9rem'
};

const numberSetStyle = {
  padding: '0.25rem 0',
  fontFamily: 'monospace'
};

const noAssignmentStyle = {
  color: '#666',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '2rem'
};

const historyControlsStyle = {
  marginBottom: '1.5rem'
};

const selectStyle = {
  width: '100%',
  padding: '0.75rem',
  fontSize: '1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: 'white'
};

const selectedAssignmentStyle = {
  backgroundColor: '#f0f9ff',
  padding: '1.5rem',
  borderRadius: '8px',
  border: '1px solid #bfdbfe'
};

export default AdminUserInfoTab;